import { ROUTES_EMPLOYEES, ROUTES_SUPERVISORS } from '@/app/data/route_constants';
import router from '@/router';
import { PROFILE_TYPES, ProfileRecord } from '@/app/data/model_constants';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { zigguParentURL, zigguSubdomainURL } from '@/app/util/urls';
import useGlobalConfirm from '@/composables/useGlobalConfirm';

export default function useProfileImpersonation() {
  const store = useStore();
  const { confirm } = useGlobalConfirm();
  const { t } = useI18n();

  // TODO: not complete as-is, see ApplicationController#authorize_impersonation, also check employee role maintainer
  const canImpersonateProfile = (current_profile, profile) =>
    current_profile.type === PROFILE_TYPES.SUPERVISOR ||
    (current_profile.type === PROFILE_TYPES.EMPLOYEE && profile.type === PROFILE_TYPES.PARTNER) ||
    (current_profile.type === PROFILE_TYPES.EMPLOYEE && profile.type === PROFILE_TYPES.CUSTOMER);

  const isProfileActive = (profile) => {
    if (_.includes(ProfileRecord.STATUSES_ACTIVE, profile.attributes.status)) return true;

    confirm({
      message: t('contact.labels.impersonateInactiveProfile'),
      header: 'Oh no!',
      accept_label: 'Got it',
    });
    return false;
  };

  const impersonationURL = (profile, tenantable) => {
    if (!canImpersonateProfile(store.getters.current_profile, profile)) return null;
    if (!isProfileActive(profile)) return null;

    const subdomain = tenantable.attributes.subdomain;
    const url_base =
      profile.type === PROFILE_TYPES.CUSTOMER
        ? zigguSubdomainURL(subdomain, `/customer/${subdomain}`)
        : zigguParentURL(`/${profile.type.toLowerCase()}/${subdomain}`);
    const query_params = `?_profile_type=${profile.type}&_profile_id=${profile.id}`;

    return `${url_base}${query_params}`;
  };

  const supervisorURL = (current_profile) => {
    const subdomain = PROFILE_TYPES.SUPERVISOR.toLowerCase();
    const url_base = zigguSubdomainURL(subdomain);
    const url_path = router.resolve({
      name: ROUTES_SUPERVISORS.USER_PROFILE_INFO,
      params: {
        user_id: current_profile.attributes.user_id,
        profile_id: current_profile.id,
        profile_type: current_profile.type,
      },
    }).href;
    return `${url_base}${url_path}`;
  };

  const impersonate = (profile, tenantable, { only_url = false, open_in_new_tab = false } = {}) => {
    try {
      const url = impersonationURL(profile, tenantable);
      if (!url) return undefined;

      if (only_url) return url;
      if (open_in_new_tab) {
        const newWindow = window.open(url, '_blank');
        if (newWindow === null) {
          throw new Error('Unable to open new window. Please check if popup blocker is enabled.');
        }
      } else {
        window.location.href = url;
      }
    } catch {
      return undefined;
    }
    return undefined;
  };

  const stopImpersonation = () => {
    sessionStorage.removeItem('_profile_id');
    sessionStorage.removeItem('_profile_type');

    const current_profile = store.getters.current_profile;
    const current_profile_true = store.getters.current_profile_true;

    if (current_profile_true.type === PROFILE_TYPES.SUPERVISOR) {
      window.location.href = supervisorURL(current_profile);
    } else if (current_profile_true.type === PROFILE_TYPES.EMPLOYEE) {
      const subdomain = store.getters.current_client.attributes.subdomain;
      const url_base = zigguParentURL();
      const url_path = router.resolve({
        name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE,
        params: {
          subdomain,
          profile_id: current_profile.id,
          profile_type: current_profile.type,
        },
      }).href;
      window.location.href = `${url_base}${url_path}`;
    }
  };

  return {
    impersonate,
    impersonationURL,
    isProfileActive,
    stopImpersonation,
    supervisorURL,
  };
}
